import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import { useIntl } from 'react-intl';
import { format, parse, setHours } from 'date-fns';
import { enUS as enLocale } from 'date-fns/locale';
import { CloseButton, EditButton } from '#/shared/components/PickerButtons';
import useManagePopperVisibility from '#/trip-planner/hooks/useManagePopperVisibility';
import { generateSequence } from '#/trip-planner/utils/date-time';
import { zipToObject } from '#/shared/utils/array';
import useIntlDatetime from '#/shared/hooks/useIntlDatetime';
import SelectionColumn from './SelectionColumn';
import * as Styled from './styled';

const PERIOD_FORMAT = 'aaa';
const DISPLAY_TIME_FORMAT = `h:mm ${PERIOD_FORMAT}`;
const MINUTES_PADDED_LENGTH = 2;
const HOURS = generateSequence(12, 1);
const MINUTES = generateSequence(60, 0, MINUTES_PADDED_LENGTH);
const HOUR_KEY = 'hour';
const MINUTE_KEY = 'minute';
const PERIOD_KEY = 'period';
const AM_DATE = setHours(new Date(), 6);
const PM_DATE = setHours(new Date(), 18);

// item order in this array is relied upon
const TIME_PART_KEYS = Object.freeze([HOUR_KEY, MINUTE_KEY, PERIOD_KEY]);

function TimePicker({
  selectedTime,
  onTimeChange,
  storedTimeFormat,
  inputId,
  ariaDescribedBy,
  className,
}) {
  const { dateFnsLocale: selectedLocale } = useIntlDatetime();

  // the period piece of the time must be in English for `date-fns` to
  // be able to parse the time into a Date object, this lookup allows a
  // localized period to be converted back its English equivalent
  const enPeriodLookup = [AM_DATE, PM_DATE].reduce((lookup, date) => {
    const [selectLangPeriod, enPeriod] = [selectedLocale, enLocale].map(
      locale => format(date, PERIOD_FORMAT, { locale })
    );

    return {
      ...lookup,
      [selectLangPeriod]: enPeriod,
    };
  }, {});

  // generated AM & PM values in the user's selected language
  const localizedPeriods = Object.keys(enPeriodLookup);

  const [focusedInputIx, setFocusedInputIx] = useState(null);
  const [focusedColumnIx, setFocusedColumnIx] = useState(null);
  const [userClickedPopper, setUserClickedPopper] = useState(false);

  const {
    popperRef,
    fieldRef,
    inputRef: inputContainerRef,
    popperVisible,
    showPopper,
    hidePopper,
    onFieldClick,
    focusTrapOptions,
  } = useManagePopperVisibility({
    onPopperShow: () => {
      setUserClickedPopper(false);
      setFocusedColumnIx(null);
    },
  });

  const scrollToSelection = popperVisible && !userClickedPopper;

  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');

  const timePartLookup = zipToObject(TIME_PART_KEYS, [
    selectedHour,
    selectedMinute,
    selectedPeriod,
  ]);

  const setTimePart = useCallback(
    (partKey, value) =>
      zipToObject(TIME_PART_KEYS, [
        setSelectedHour,
        setSelectedMinute,
        setSelectedPeriod,
      ])[partKey](value),
    []
  );

  const getTimePartsFromDate = useCallback(
    date =>
      zipToObject(
        TIME_PART_KEYS,
        format(date, DISPLAY_TIME_FORMAT, {
          locale: selectedLocale,
        }).split(/:|\s/)
      ),
    [selectedLocale]
  );

  useEffect(() => {
    if (selectedTime) {
      Object.entries(
        getTimePartsFromDate(parse(selectedTime, storedTimeFormat, new Date()))
      ).forEach(partEntry => setTimePart(...partEntry));
    }
  }, [getTimePartsFromDate, selectedTime, setTimePart, storedTimeFormat]);

  // these values need to be stored between renders, but changes should
  // not trigger a rerender so a ref is used instead of a state node
  const popperDefaults = useRef({});

  const getPopperTimePartValue = partKey => {
    let partValue = timePartLookup[partKey];

    // if a time part is not set the corresponding column on the popper will
    // display the current time (part)
    if (!partValue && popperVisible && !userClickedPopper) {
      partValue = getTimePartsFromDate(new Date())[partKey];
      popperDefaults.current[partKey] = partValue;
    }

    return partValue;
  };

  const assembleTimeParts = (
    hour = selectedHour,
    minute = selectedMinute,
    period = selectedPeriod
  ) => `${hour}:${minute} ${period}`;

  const getTimePartUpdater = (partKey, forPopper = false) => {
    return value => {
      setTimePart(partKey, value);

      // if the part is being updated from the popper and any time part is
      // unset integrate the defaults (usually the current time) for all columns
      const usePopperDefaults =
        forPopper && Object.values(timePartLookup).some(p => !p);

      const {
        [HOUR_KEY]: hourPart,
        [MINUTE_KEY]: minutePart,
        [PERIOD_KEY]: periodPart,
      } = {
        ...(usePopperDefaults ? popperDefaults.current : {}),
        // integrate selected time parts excluding items with falsy values
        ...Object.entries(timePartLookup)
          .filter(entry => !!entry[1])
          .reduce((obj, [k, v]) => ({ ...obj, [k]: v }), {}),
        [partKey]: value,
      };

      if (hourPart && minutePart && periodPart) {
        // set the time by combining the time parts
        onTimeChange(
          format(
            parse(
              assembleTimeParts(
                hourPart,
                minutePart,
                enPeriodLookup[periodPart]
              ),
              DISPLAY_TIME_FORMAT,
              new Date()
            ),
            storedTimeFormat
          )
        );
      }
    };
  };

  const focusedColumnRef = useRef();
  const columnContainerRef = useRef();
  const focusedInputRef = useRef();

  /**
   * Get a sibling Element of the supplied  `element`
   *
   * @param {Element} element
   * @param {boolean} previous If `true` the previous sibling will be returned
   * instead of the next
   * @param {string[]} ignoreAttributes If a sibling element has any of the
   * attributes in this array it will be skipped and the function will traverse
   * to the next sibling in the direction it has been traveling
   * @returns {Element|null}
   */
  const getSiblingTimePart = (
    element,
    previous = false,
    ignoreAttributes = ['data-ignore-as-sibling', 'data-proxy-element']
  ) => {
    const sibling =
      element?.[previous === true ? 'previousSibling' : 'nextSibling'];

    if (sibling) {
      return ignoreAttributes.some(a => sibling.hasAttribute(a))
        ? getSiblingTimePart(sibling, previous)
        : sibling;
    }

    return null;
  };

  const focusSiblingTimePart = (elementRef, previous = false) => {
    const element = elementRef?.current;

    if (element) {
      const sibling = getSiblingTimePart(element, previous);
      if (sibling) sibling.focus();
    }
  };

  /**
   * Handler for key down event for time part (`TimePartInput` or
   * `SelectionColumn`) that allows the user to change the part that focused
   * using the left and right arrow keys
   *
   * @param {KeyboardEvent} e
   * @param {Object} elementRef `ref` object containing reference to time part
   * element
   */
  const onTimePartKeyDown = (e, elementRef) => {
    const { key } = e;

    if (key === 'ArrowRight') {
      focusSiblingTimePart(elementRef);
    } else if (key === 'ArrowLeft') {
      focusSiblingTimePart(elementRef, true);
    }
  };

  const onPopperKeyDown = e => {
    if (e.key === 'Enter') {
      setUserClickedPopper(true);
    }

    onTimePartKeyDown(e, focusedColumnRef);
  };

  const timePartSeparatorLookup = { [HOUR_KEY]: ':', [MINUTE_KEY]: ' ' };
  const intl = useIntl();

  const timePartItemsConfig = [
    {
      key: HOUR_KEY,
      values: HOURS,
      input: {
        ariaLabelIntlId: 'planner.time-input-hour-aria',

        // TODO: this attribute should live on an element that describes
        //  the full time, not just hours, but the component needs a11y
        //  refactoring for that to be possible
        ariaDescribedBy,
      },
      column: {
        ariaLabelIntlId: 'planner.time-column-hours-aria',
      },
    },
    {
      key: MINUTE_KEY,
      values: MINUTES,
      numberToValue: n =>
        n != null ? String(n).padStart(MINUTES_PADDED_LENGTH, '0') : '',
      input: {
        ariaLabelIntlId: 'planner.time-input-minute-aria',
      },
      column: {
        ariaLabelIntlId: 'planner.time-column-minutes-aria',
      },
    },
    {
      key: PERIOD_KEY,
      values: localizedPeriods,
      numberToValue: n => ({ ...localizedPeriods }[n] || ''),
      valueToNumber: vParam =>
        localizedPeriods.reduce((obj, v, ix) => ({ ...obj, [v[0]]: ix }), {})[
          vParam.charAt(0).toLowerCase()
        ],
      input: {
        ariaLabelIntlId: 'planner.time-period-aria',
        inputMode: 'text',
      },
      column: {
        ariaLabelIntlId: 'planner.time-period-aria',
      },
    },
  ];

  return (
    <Styled.TimePickerContainer className={className}>
      <Styled.TimeFieldContainer
        popperVisible={popperVisible}
        onClick={onFieldClick}
        ref={fieldRef}
      >
        <Styled.TimeInputGroupContainer ref={inputContainerRef}>
          <input
            // this element exists for accessibility; it allows the full time
            // to be read from one element instead being assembled from three
            type="hidden"
            value={assembleTimeParts()}
            aria-hidden={false}
            id={inputId}
          />
          {timePartItemsConfig.map((item, ix) => {
            const separator = timePartSeparatorLookup[item.key];

            return (
              <Fragment key={item.key}>
                <Styled.TimePartInput
                  value={timePartLookup[item.key]}
                  onChange={getTimePartUpdater(item.key)}
                  onFocus={() => setFocusedInputIx(ix)}
                  onKeyDown={e => onTimePartKeyDown(e, focusedInputRef)}
                  onFieldPopulated={() => focusSiblingTimePart(focusedInputRef)}
                  minValue={item.values[0]}
                  maxValue={item.values.slice(-1)[0]}
                  numberToValue={item.numberToValue}
                  valueToNumber={item.valueToNumber}
                  inputMode={item.input.inputMode}
                  ariaLabel={intl.formatMessage({
                    id: item.input.ariaLabelIntlId,
                  })}
                  ariaDescribedBy={item.input.ariaDescribedBy}
                  inputRef={ix === focusedInputIx ? focusedInputRef : undefined}
                  required
                />
                {separator && <span data-ignore-as-sibling>{separator}</span>}
              </Fragment>
            );
          })}
        </Styled.TimeInputGroupContainer>
        <EditButton
          Icon={Styled.ClockIcon}
          onClick={showPopper}
          buttonAriaLabelIntlId="planner.time-picker-aria"
          iconAriaLabelIntlId="planner.clock-icon-aria"
        />
      </Styled.TimeFieldContainer>
      {popperVisible && (
        <FocusTrap focusTrapOptions={focusTrapOptions}>
          <Styled.PopperWrapper ref={popperRef}>
            <Styled.PopperContainer
              onClick={() => setUserClickedPopper(true)}
              onKeyDown={onPopperKeyDown}
              role="dialog"
              aria-modal
              aria-label={intl.formatMessage({
                id: 'planner.time-picker-aria',
              })}
              popperVisible={popperVisible}
            >
              <Styled.ColumnContainer
                onBlur={e => {
                  if (!columnContainerRef.current.contains(e.relatedTarget))
                    setFocusedColumnIx(null);
                }}
                ref={columnContainerRef}
              >
                {timePartItemsConfig.map((item, ix) => (
                  <SelectionColumn
                    key={item.key}
                    items={item.values}
                    selectedValue={getPopperTimePartValue(item.key)}
                    onSelection={getTimePartUpdater(item.key, true)}
                    onFocus={() => setFocusedColumnIx(ix)}
                    scrollToSelection={scrollToSelection}
                    ariaLabel={intl.formatMessage({
                      id: item.column.ariaLabelIntlId,
                    })}
                    columnRef={
                      ix === focusedColumnIx ? focusedColumnRef : undefined
                    }
                  />
                ))}
              </Styled.ColumnContainer>
              <CloseButton onClick={hidePopper} />
            </Styled.PopperContainer>
          </Styled.PopperWrapper>
        </FocusTrap>
      )}
    </Styled.TimePickerContainer>
  );
}

TimePicker.propTypes = {
  /** Time to be displayed on the picker component */
  selectedTime: PropTypes.string.isRequired,
  /** Handler fired when time is changed, selected value is passed as param */
  onTimeChange: PropTypes.func.isRequired,
  /** Format time expected as an input and emitted in `onTimeChange` */
  storedTimeFormat: PropTypes.string,
  /** ID input element in this component that holds the full time */
  inputId: PropTypes.string,
  /** ID for element that describes the value in the time input */
  ariaDescribedBy: PropTypes.string,
  /** Pass-through to allow for styling via styled-components */
  className: PropTypes.string,
};

TimePicker.defaultProps = {
  storedTimeFormat: 'HH:mm',
  inputId: undefined,
  ariaDescribedBy: undefined,
  className: null,
};

export default TimePicker;
export { Styled };
